import fs from 'node:fs'
import path from 'node:path'
import * as p from '@clack/prompts'
import type { GenerateCommandOptions } from '@core-engine/commands/runtime.js'
import type { ResolvedBetterstartConfig } from '@core-engine/config/types.js'
import { walkFields } from '@core-engine/schema/field-helpers/index.js'
import type { LoadedSchema } from '@core-engine/schema/schema-reader.js'
import {
  listSchemaNames,
  loadSchema,
  SchemaNotFoundError,
  validateLoadedSchema
} from '@core-engine/schema/schema-reader.js'
import {
  buildCustomCellRenameSelection,
  buildRenamePreviewPaths,
  formatRenameSelectionSummary,
  type RenamePlan,
  transformContentForRenamePlan
} from '@core-engine/snapshots/ast-substitution.js'
import { ensureSnapshotGitFiles } from '@core-engine/snapshots/gitignore.js'
import {
  detectRenameCandidates,
  detectStringRenameCandidates
} from '@core-engine/snapshots/rename-detector.js'
import { diffSchemas } from '@core-engine/snapshots/schema-diff.js'
import {
  BARREL_SCOPE,
  clearTombstone,
  hasTombstone,
  loadManifest,
  snapshotRootExists
} from '@core-engine/snapshots/store.js'
import { detectPackageManager } from '@core-engine/utils/package-manager.js'
import { spinner } from '@core-engine/utils/spinner.js'
import { toKebabCase } from '@core-engine/utils/string.js'
import { resolveAdminNamespace } from '@next-adapter/config/namespace.js'
import { resolveProjectPaths } from '@next-adapter/config/paths.js'
import { resolveConfig } from '@next-adapter/config.js'
import { runFormPipeline } from '@next-adapter/generators/form-pipeline/index.js'
import { runEntityPipeline, runSinglePipeline } from '@next-adapter/generators/index.js'
import { runPostGenerate, scanConflictPaths } from '@next-adapter/generators/post-generate.js'
import { installDependenciesAsync } from '@next-adapter/init/scaffolders/dependencies.js'
import { hasInstalledIntegrationFeature } from '@next-adapter/integration-runtime.js'
import { ensureUserSchemaOwnership } from '@next-adapter/preset-runtime.js'
import { applyGeneratedFiles } from '@next-adapter/snapshots/apply.js'
import { renderBarrelFiles } from '@next-adapter/snapshots/barrels.js'
import { getSchemaOwner } from '@preset-engine/ownership.js'

function isInteractiveSession(): boolean {
  return Boolean(process.stdin.isTTY) && process.env.CI !== 'true'
}

function isStoredSchemaJson(
  schemaJson: { name: string } | LoadedSchema['schema']
): schemaJson is LoadedSchema['schema'] {
  return 'fields' in schemaJson
}

const MARKDOWN_IT_RENDERER_SPEC = 'markdown-it@^13.0.2'
const MARKDOWN_RENDERER_DEPS = [
  'katex',
  MARKDOWN_IT_RENDERER_SPEC,
  'markdown-it-dollarmath',
  'sanitize-html'
]
const MARKDOWN_RENDERER_DEV_DEPS = ['@types/markdown-it@13', '@types/sanitize-html']

interface ProjectPackageJson {
  dependencies?: Record<string, unknown>
  devDependencies?: Record<string, unknown>
}

function getGeneratedSchemaRoutePath(loaded: LoadedSchema, adminRoutePath: string): string {
  const schemaPath = toKebabCase(loaded.schema.name)
  return loaded.type === 'form'
    ? `${adminRoutePath}/forms/${schemaPath}`
    : `${adminRoutePath}/${schemaPath}`
}

function readPackageJson(cwd: string): ProjectPackageJson | null {
  const pkgPath = path.join(cwd, 'package.json')
  if (!fs.existsSync(pkgPath)) return null

  try {
    return JSON.parse(fs.readFileSync(pkgPath, 'utf-8')) as ProjectPackageJson
  } catch {
    return null
  }
}

function packageNameFromSpec(spec: string): string {
  if (spec.startsWith('@')) {
    const versionSeparator = spec.indexOf('@', 1)
    return versionSeparator === -1 ? spec : spec.slice(0, versionSeparator)
  }

  const versionSeparator = spec.indexOf('@')
  return versionSeparator === -1 ? spec : spec.slice(0, versionSeparator)
}

function hasDependency(pkg: ProjectPackageJson | null, name: string, runtimeOnly = false): boolean {
  if (!pkg) return false
  if (Object.hasOwn(pkg.dependencies ?? {}, name)) return true
  return runtimeOnly ? false : Object.hasOwn(pkg.devDependencies ?? {}, name)
}

function hasCompatibleMarkdownItDependency(pkg: ProjectPackageJson | null): boolean {
  const spec = pkg?.dependencies?.['markdown-it']
  return typeof spec === 'string' && /^(?:workspace:|link:|file:|portal:|\^?13\.|~13\.)/.test(spec)
}

function schemaNeedsMarkdownRenderer(loaded: LoadedSchema): boolean {
  if (loaded.type === 'form') return false

  let needsRenderer = false

  walkFields(loaded.schema.fields, (field) => {
    if ((field.type === 'richtext' || field.type === 'markdown') && field.output === 'html') {
      needsRenderer = true
    }
  })

  return needsRenderer
}

async function ensureMarkdownRendererDependencies(cwd: string, required: boolean): Promise<void> {
  if (!required) return

  const pkg = readPackageJson(cwd)
  const dependencies = MARKDOWN_RENDERER_DEPS.filter((spec) =>
    spec === MARKDOWN_IT_RENDERER_SPEC
      ? !hasCompatibleMarkdownItDependency(pkg)
      : !hasDependency(pkg, packageNameFromSpec(spec), true)
  )
  const devDependencies = MARKDOWN_RENDERER_DEV_DEPS.filter(
    (spec) => !hasDependency(pkg, packageNameFromSpec(spec))
  )

  if (dependencies.length === 0 && devDependencies.length === 0) return

  const pm = detectPackageManager(cwd)
  const s = spinner()
  s.start('Installing markdown renderer dependencies')
  const result = await installDependenciesAsync({
    cwd,
    pm,
    dependencies,
    devDependencies
  })

  if (!result.success) {
    s.stop('Failed to install markdown renderer dependencies')
    throw new Error(result.error ?? 'Failed to install markdown renderer dependencies.')
  }

  s.stop(
    `Installed markdown renderer dependencies (${dependencies.length} deps + ${devDependencies.length} dev deps)`
  )
}

function assertSnapshotStateOrExit(cwd: string): void {
  if (snapshotRootExists(cwd)) {
    return
  }

  console.error(
    '  Project state is unsupported or incomplete. Re-run `betterstart init --force` to create the current snapshot-backed layout.'
  )
  process.exit(1)
}

function printSchemaDiff(scope: string, loaded: LoadedSchema, cwd: string): void {
  const manifest = loadManifest(cwd, scope)
  if (!manifest || scope === BARREL_SCOPE || !isStoredSchemaJson(manifest.schemaJson)) {
    return
  }

  const diff = diffSchemas(manifest.schemaJson, loaded.schema)
  const renameCandidates = detectRenameCandidates(diff.removed, diff.added)

  if (
    diff.added.length === 0 &&
    diff.removed.length === 0 &&
    diff.changed.length === 0 &&
    diff.customComponents.added.length === 0 &&
    diff.customComponents.removed.length === 0
  ) {
    return
  }

  console.log(`  Schema diff for ${scope}:`)
  if (diff.added.length > 0) {
    console.log(`    + fields: ${diff.added.map((field) => field.name).join(', ')}`)
  }
  if (diff.removed.length > 0) {
    console.log(`    - fields: ${diff.removed.map((field) => field.name).join(', ')}`)
  }
  if (diff.changed.length > 0) {
    console.log(
      `    ~ fields: ${diff.changed.map((entry) => `${entry.before.name}:${entry.before.type}->${entry.after.type}`).join(', ')}`
    )
  }
  if (renameCandidates.length > 0) {
    console.log(
      `    ? rename candidates: ${renameCandidates.map((entry) => `${entry.before}->${entry.after}`).join(', ')}`
    )
  }
  if (diff.customComponents.added.length > 0 || diff.customComponents.removed.length > 0) {
    console.log(
      `    custom cells: +[${diff.customComponents.added.join(', ')}] -[${diff.customComponents.removed.join(', ')}]`
    )
  }
}

async function promptConfirm(message: string): Promise<boolean> {
  const result = await p.confirm({
    message,
    initialValue: true
  })

  if (p.isCancel(result)) {
    throw new Error('Generation cancelled.')
  }

  return Boolean(result)
}

function readLocalProjectFile(cwd: string, filePath: string): string | null {
  const fullPath = path.join(cwd, ...filePath.split('/'))
  if (!fs.existsSync(fullPath)) {
    return null
  }

  return fs.readFileSync(fullPath, 'utf-8')
}

function validateCustomCellRenameSelections(
  cwd: string,
  selected: RenamePlan['customCells'],
  fieldRenames: RenamePlan['fields']
): { renames: RenamePlan['customCells']; warnings: string[] } {
  const renames: RenamePlan['customCells'] = []
  const warnings: string[] = []

  for (const rename of selected) {
    const validationPlan: RenamePlan = {
      fields: fieldRenames,
      customCells: [rename]
    }
    const validationPaths = [rename.columnsPath, rename.oldPath]
    let valid = true

    for (const validationPath of validationPaths) {
      const content = readLocalProjectFile(cwd, validationPath)
      if (content == null) {
        continue
      }

      const result = transformContentForRenamePlan(content, validationPath, validationPlan)
      if (result.parseFailed) {
        warnings.push(
          `Skipped custom cell rename ${rename.before} -> ${rename.after}: AST parse failed for ${validationPath}`
        )
        valid = false
        break
      }
    }

    if (valid) {
      renames.push(rename)
    }
  }

  return { renames, warnings }
}

function collectRenamePreview(
  cwd: string,
  remotePaths: string[],
  plan: RenamePlan
): { previewLines: string[]; warnings: string[] } {
  const previewLines: string[] = []
  const warnings: string[] = []

  for (const previewPath of buildRenamePreviewPaths(remotePaths, plan)) {
    const content = readLocalProjectFile(cwd, previewPath.sourcePath)
    if (content == null) {
      continue
    }

    const result = transformContentForRenamePlan(
      content,
      previewPath.sourcePath,
      plan,
      previewPath.displayPath
    )

    if (result.parseFailed) {
      warnings.push(
        `AST rename preview skipped for ${previewPath.displayPath}: parse failed, merge will fall back for that file`
      )
      continue
    }

    for (const change of result.previewChanges) {
      previewLines.push(
        `    ${change.path}:${change.line}\n      - ${change.before || '(empty)'}\n      + ${change.after || '(empty)'}`
      )
    }
  }

  return { previewLines, warnings }
}

async function maybeBuildRenamePlan(
  loaded: LoadedSchema,
  cwd: string,
  config: ResolvedBetterstartConfig,
  generatedFiles: Array<{ path: string; content: string }>,
  options: { force: boolean; interactive: boolean }
): Promise<RenamePlan | undefined> {
  if (!options.interactive || options.force) {
    return undefined
  }

  const manifest = loadManifest(cwd, loaded.schema.name)
  if (!manifest || !isStoredSchemaJson(manifest.schemaJson)) {
    return undefined
  }

  const diff = diffSchemas(manifest.schemaJson, loaded.schema)
  const fieldCandidates = detectRenameCandidates(diff.removed, diff.added)
  const customCellCandidates = detectStringRenameCandidates(
    diff.customComponents.removed,
    diff.customComponents.added
  )

  if (fieldCandidates.length === 0 && customCellCandidates.length === 0) {
    return undefined
  }

  const plan: RenamePlan = {
    fields: [],
    customCells: []
  }

  for (const candidate of fieldCandidates) {
    const accepted = await promptConfirm(
      `Field '${candidate.before}' was removed and '${candidate.after}' was added. Treat it as a rename before merge?`
    )

    if (accepted) {
      plan.fields.push(candidate)
    }
  }

  if (customCellCandidates.length > 0) {
    const paths = resolveProjectPaths(config)
    for (const candidate of customCellCandidates) {
      const accepted = await promptConfirm(
        `Custom cell '${candidate.before}' was removed and '${candidate.after}' was added. Treat it as a rename before merge?`
      )

      if (accepted) {
        plan.customCells.push(
          buildCustomCellRenameSelection(
            loaded.schema.name,
            paths.pagesDir,
            candidate.before,
            candidate.after
          )
        )
      }
    }
  }

  const validatedCustomCells = validateCustomCellRenameSelections(
    cwd,
    plan.customCells,
    plan.fields
  )
  const finalPlan: RenamePlan = {
    fields: plan.fields,
    customCells: validatedCustomCells.renames
  }

  if (finalPlan.fields.length === 0 && finalPlan.customCells.length === 0) {
    for (const warning of validatedCustomCells.warnings) {
      console.warn(`  ${warning}`)
    }
    return undefined
  }

  const preview = collectRenamePreview(
    cwd,
    generatedFiles.map((file) => file.path),
    finalPlan
  )

  console.log('  Proposed rename substitutions:')
  for (const line of formatRenameSelectionSummary(finalPlan)) {
    console.log(`    ${line}`)
  }

  const previewLines = preview.previewLines.slice(0, 20)
  for (const line of previewLines) {
    console.log(line)
  }

  if (preview.previewLines.length > previewLines.length) {
    console.log(`    ... ${preview.previewLines.length - previewLines.length} more change(s)`)
  }

  for (const warning of [...validatedCustomCells.warnings, ...preview.warnings]) {
    console.warn(`  ${warning}`)
  }

  const confirmed = await promptConfirm('Apply these rename substitutions before merge?')
  return confirmed ? finalPlan : undefined
}

function validateIntegrationRequirements(
  loaded: LoadedSchema,
  config: ResolvedBetterstartConfig
): void {
  if (
    loaded.type === 'form' &&
    loaded.schema.mailchimp &&
    !hasInstalledIntegrationFeature(config, 'mailchimp')
  ) {
    throw new Error(
      `Form schema "${loaded.schema.name}" sets "mailchimp", but the mailchimp integration is not installed. Run \`betterstart add --integration mailchimp\` or remove the "mailchimp" property from the schema.`
    )
  }
}

function runPipeline(
  loaded: LoadedSchema,
  cwd: string,
  config: ResolvedBetterstartConfig,
  options: { force: boolean }
) {
  const owner = getSchemaOwner(cwd, loaded.schema.name)
  const outputNamespace =
    owner === 'core'
      ? 'core'
      : owner?.startsWith('preset:')
        ? owner.replace(/^preset:/, '')
        : undefined

  if (loaded.type === 'form') {
    return runFormPipeline(loaded.schema, cwd, config, {
      force: options.force,
      silent: true,
      outputNamespace
    })
  }

  if (loaded.type === 'single') {
    return runSinglePipeline(loaded.schema, cwd, config, {
      force: options.force,
      silent: true,
      outputNamespace
    })
  }

  return runEntityPipeline(loaded.schema, cwd, config, {
    force: options.force,
    silent: true,
    outputNamespace
  })
}

async function applySchemaGeneration(
  loaded: LoadedSchema,
  cwd: string,
  config: ResolvedBetterstartConfig,
  options: { force: boolean; acceptGenerated?: boolean; interactive: boolean }
) {
  const scope = loaded.schema.name
  const tombstoneCleared = hasTombstone(cwd, scope)
  if (tombstoneCleared) {
    clearTombstone(cwd, scope)
  }

  validateIntegrationRequirements(loaded, config)
  printSchemaDiff(scope, loaded, cwd)

  const pipelineResult = runPipeline(loaded, cwd, config, options)
  if (!pipelineResult.success) {
    throw new Error(pipelineResult.errors.join('; '))
  }

  const renamePlan = await maybeBuildRenamePlan(loaded, cwd, config, pipelineResult.files, options)

  const summary = await applyGeneratedFiles({
    cwd,
    config,
    scope,
    schemaJson: loaded.schema,
    generatedFiles: pipelineResult.files,
    force: options.force,
    acceptGenerated: options.acceptGenerated,
    interactive: options.interactive,
    renamePlan
  })

  if (tombstoneCleared) {
    summary.tombstoneCleared = scope
  }

  return {
    fileCount: pipelineResult.files.length,
    summary
  }
}

async function applyBarrels(
  cwd: string,
  config: ResolvedBetterstartConfig,
  options: { force: boolean; acceptGenerated?: boolean; interactive: boolean }
) {
  return applyGeneratedFiles({
    cwd,
    config,
    scope: BARREL_SCOPE,
    schemaJson: { name: BARREL_SCOPE },
    generatedFiles: renderBarrelFiles(cwd, config),
    force: options.force,
    acceptGenerated: options.acceptGenerated,
    interactive: options.interactive
  })
}

function printApplySummary(
  schemaName: string,
  summary: Awaited<ReturnType<typeof applySchemaGeneration>>['summary']
): void {
  console.log(`  ${schemaName}:`)
  if (summary.tombstoneCleared) {
    console.log(`    Tombstone cleared: ${summary.tombstoneCleared}`)
  }
  if (summary.writtenFresh.length > 0) {
    console.log(`    written fresh: ${summary.writtenFresh.length}`)
  }
  if (summary.overwritten.length > 0) {
    console.log(`    overwritten: ${summary.overwritten.length}`)
  }
  if (summary.mergedCleanly.length > 0) {
    console.log(`    merged cleanly: ${summary.mergedCleanly.length}`)
  }
  if (summary.acceptedGenerated.length > 0) {
    console.log(`    accepted generated: ${summary.acceptedGenerated.length}`)
  }
  if (summary.conflictPaths.length > 0) {
    console.log(`    with conflicts: ${summary.conflictPaths.length}`)
  }
  if (summary.orphanedDeleted.length > 0) {
    console.log(`    orphaned deleted: ${summary.orphanedDeleted.length}`)
  }
  if (summary.orphanedKept.length > 0) {
    console.log(`    orphaned kept: ${summary.orphanedKept.length}`)
  }
  if (summary.skipped.length > 0) {
    console.log(`    skipped: ${summary.skipped.join(', ')}`)
  }
  if (summary.skippedCleared.length > 0) {
    console.log(`    skip entries cleared: ${summary.skippedCleared.join(', ')}`)
  }
}

export async function runGenerateCommand(
  schemaName: string | undefined,
  options: GenerateCommandOptions
): Promise<void> {
  const cwd = options.cwd ? path.resolve(options.cwd) : process.cwd()
  const interactive = options.all
    ? options.interactive && isInteractiveSession()
    : isInteractiveSession()

  let config: ResolvedBetterstartConfig
  try {
    config = await resolveConfig(cwd)
  } catch (error) {
    console.error(
      `  Error loading config: ${error instanceof Error ? error.message : String(error)}`
    )
    process.exit(1)
  }

  assertSnapshotStateOrExit(cwd)
  ensureSnapshotGitFiles(cwd)

  const paths = resolveProjectPaths(config)
  const schemasDir = path.join(cwd, ...paths.schemasDir.split('/'))

  if (options.all) {
    const schemaNames = listSchemaNames(schemasDir)
    if (schemaNames.length === 0) {
      console.error(`\n  No schemas found in ${schemasDir}\n`)
      process.exit(1)
    }

    const failed: string[] = []
    const processed: string[] = []
    const processedRoutes: string[] = []
    let markdownDepsChecked = false
    const adminRoutePath = resolveAdminNamespace(config.frameworkConfig.next.namespace).routePath

    console.log(`\n  BetterStart Generator — regenerating ${schemaNames.length} schema(s)\n`)

    for (const name of schemaNames) {
      if (hasTombstone(cwd, name)) {
        if (loadManifest(cwd, name)) {
          clearTombstone(cwd, name)
          console.warn(`  - ${name}: tombstone cleared (manifest present)`)
        } else {
          console.log(`  - ${name}: skipped (tombstoned)`)
          continue
        }
      }

      try {
        const loaded = loadSchema(schemasDir, name)
        validateIntegrationRequirements(loaded, config)
        const validationErrors = validateLoadedSchema(loaded)
        if (validationErrors.length > 0) {
          throw new Error(validationErrors.join('; '))
        }

        const needsMarkdownRenderer = schemaNeedsMarkdownRenderer(loaded)
        if (!markdownDepsChecked && needsMarkdownRenderer) {
          await ensureMarkdownRendererDependencies(cwd, true)
          markdownDepsChecked = true
        }

        const result = await applySchemaGeneration(loaded, cwd, config, {
          force: options.force,
          acceptGenerated: options.acceptGenerated,
          interactive
        })
        ensureUserSchemaOwnership(cwd, name)
        printApplySummary(name, result.summary)
        processed.push(name)
        processedRoutes.push(getGeneratedSchemaRoutePath(loaded, adminRoutePath))
      } catch (error) {
        console.error(`  ✗ ${name}: ${error instanceof Error ? error.message : String(error)}`)
        failed.push(name)
      }
    }

    if (processed.length > 0) {
      const barrelSummary = await applyBarrels(cwd, config, {
        force: options.force,
        acceptGenerated: options.acceptGenerated,
        interactive
      })
      printApplySummary(BARREL_SCOPE, barrelSummary)
    }

    const conflictPaths = scanConflictPaths(cwd)
    if (processed.length > 0) {
      await runPostGenerate(cwd, processed[0] ?? 'admin', {
        skipMigration: options.skipMigration,
        hasConflicts: conflictPaths.length > 0,
        conflictPaths,
        adminRoutePath,
        schemaRoutePath: processedRoutes[0]
      })
    }

    if (failed.length > 0) {
      console.error(`\n  Failed: ${failed.join(', ')}`)
      process.exit(1)
    }

    console.log('')
    return
  }

  if (!schemaName) {
    console.error('  Error: schema name is required (or use --all)')
    process.exit(1)
  }

  let loaded: LoadedSchema
  try {
    loaded = loadSchema(schemasDir, schemaName)
  } catch (error) {
    if (error instanceof SchemaNotFoundError) {
      console.error(`  ${error.message}`)
    } else {
      console.error(
        `  Error loading schema: ${error instanceof Error ? error.message : String(error)}`
      )
    }
    process.exit(1)
  }

  const validationErrors = validateLoadedSchema(loaded)
  if (validationErrors.length > 0) {
    console.error('  Schema validation failed:')
    for (const error of validationErrors) {
      console.error(`    - ${error}`)
    }
    process.exit(1)
  }

  console.log(`\n  BetterStart Generator — ${loaded.schema.name}\n`)
  const needsMarkdownRenderer = schemaNeedsMarkdownRenderer(loaded)
  await ensureMarkdownRendererDependencies(cwd, needsMarkdownRenderer)
  const result = await applySchemaGeneration(loaded, cwd, config, {
    force: options.force,
    acceptGenerated: options.acceptGenerated,
    interactive
  })
  ensureUserSchemaOwnership(cwd, loaded.schema.name)
  printApplySummary(loaded.schema.name, result.summary)

  const barrelSummary = await applyBarrels(cwd, config, {
    force: options.force,
    acceptGenerated: options.acceptGenerated,
    interactive
  })
  printApplySummary(BARREL_SCOPE, barrelSummary)

  const conflictPaths = scanConflictPaths(cwd)
  const adminRoutePath = resolveAdminNamespace(config.frameworkConfig.next.namespace).routePath
  await runPostGenerate(cwd, loaded.schema.name, {
    skipMigration: options.skipMigration,
    hasConflicts: conflictPaths.length > 0,
    conflictPaths,
    adminRoutePath,
    schemaRoutePath: getGeneratedSchemaRoutePath(loaded, adminRoutePath)
  })
  console.log('')
}
